/**
 * PDP delivery quotes use an independent Shopify calculation, never the basket
 * cookie or a temporary add/remove. No checkout URL, analytics event or storage.
 */
(() => {
  'use strict';
  const CREATE = `mutation ETDeliveryQuote($input: CartInput!) {
    cartCreate(input: $input) {
      cart { id totalQuantity lines(first: 250) { nodes {
        quantity attributes { key value }
        merchandise { ... on ProductVariant { id } }
        sellingPlanAllocation { sellingPlan { id } }
      } pageInfo { hasNextPage } } }
      userErrors { message } warnings { code message }
    }
  }`;
  const RATES = `query ETDeliveryQuoteRates($id: ID!) {
    cart(id: $id) { totalQuantity ...ETCarrierRates @defer }
  }
  fragment ETCarrierRates on Cart {
    deliveryGroups(first: 10, withCarrierRates: true) {
      nodes { groupType deliveryOptions {
        title code deliveryMethodType estimatedCost { amount currencyCode }
      } }
      pageInfo { hasNextPage }
    }
  }`;
  const states = new Set(['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA']);

  function boot() {
    document.querySelectorAll('[data-pdp-delivery]').forEach(bind);
  }
  function bind(panel) {
    if (panel.dataset.bound || !window.ETDeliveryCore) return;
    const section = panel.closest('[data-etr-pdp]');
    const productForm = document.getElementById(panel.dataset.productForm);
    const form = panel.querySelector('[data-pdp-delivery-form]');
    const status = panel.querySelector('[data-pdp-delivery-status]');
    const results = panel.querySelector('[data-pdp-delivery-results]');
    if (!section || !productForm || !form || !status || !results) return;
    let variants;
    try { variants = JSON.parse(section.querySelector('[data-etr-product-data]').textContent).variants; }
    catch { return; }
    const core = window.ETDeliveryCore;
    const postcode = form.elements.postcode;
    const province = form.elements.province;
    const button = form.querySelector('button');
    const disclosure = panel.querySelector('[data-pdp-delivery-disclosure]');
    const title = panel.querySelector('[data-pdp-delivery-title]');
    const summary = panel.querySelector('[data-pdp-delivery-summary]');
    const action = panel.querySelector('[data-pdp-delivery-action]');
    let hasSummary = false;
    let controller;
    let sequence = 0;
    let stamp = '';
    panel.dataset.bound = 'true';
    form.hidden = false;

    // Keep native disclosure keyboard behaviour and do not move focus after a quote.
    function updateAction() {
      if (action) action.textContent = disclosure?.open ? panel.dataset.close :
        (hasSummary ? panel.dataset.change : panel.dataset.check);
    }
    function clearSummary() {
      hasSummary = false;
      if (title) title.textContent = panel.dataset.heading;
      if (summary) { summary.replaceChildren(); summary.hidden = true; }
      updateAction();
    }
    function showSummary(groups, zip, state, money) {
      if (!title || !summary) return;
      title.textContent = panel.dataset.summaryDestination.replace('__POSTCODE__', zip).replace('__STATE__', state);
      if (groups.length === 1) {
        const options = groups[0].options;
        const cheapest = options.reduce((lowest, option) => option.cents < lowest.cents ? option : lowest);
        const price = (options.length > 1 ? panel.dataset.summaryFrom : '') + money.format(cheapest.cents / 100);
        summary.textContent = panel.dataset.summaryRate.replace('__TITLE__', cheapest.title).replace('__PRICE__', price);
      } else {
        // Never combine separate shipments or imply one group's cost covers the basket.
        summary.textContent = panel.dataset.summaryMultiple;
      }
      const scope = document.createElement('span');
      scope.className = 'etr-delivery__scope';
      scope.textContent = panel.dataset.summaryScope;
      summary.append(scope);
      summary.hidden = false;
      hasSummary = true;
      updateAction();
    }
    disclosure?.addEventListener('toggle', updateAction);
    updateAction();

    function selection() {
      const id = productForm.querySelector('[name="id"]')?.value;
      const variant = variants.find(item => String(item.id) === String(id));
      const quantity = Number(productForm.querySelector('[name="quantity"]')?.value);
      const plan = new FormData(productForm).get('selling_plan');
      return { variant, variantId: id, quantity, sellingPlanId: plan ? String(plan) : '' };
    }
    function signature() {
      const chosen = selection();
      return JSON.stringify([chosen.variantId, chosen.quantity, chosen.sellingPlanId]);
    }
    function cancel(stale = false) {
      const hadQuote = results.childElementCount || status.textContent;
      sequence += 1;
      controller?.abort();
      controller = null;
      results.replaceChildren();
      results.hidden = true;
      clearSummary();
      status.textContent = stale && hadQuote ? panel.dataset.stale : '';
      panel.setAttribute('aria-busy', 'false');
      button.disabled = false;
    }
    function changed() {
      const next = signature();
      if (next !== stamp) {
        cancel(true);
        stamp = next;
      }
      const {variant} = selection();
      panel.hidden = !variant?.available || !variant?.requiresShipping;
    }
    async function request(query, variables, operationName, signal) {
      const response = await fetch('/api/2026-10/graphql.json', {
        method: 'POST', credentials: 'omit', cache: 'no-store', signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json, multipart/mixed' },
        body: JSON.stringify({ query, variables, operationName }),
      });
      if (!response.ok) throw new Error('Delivery calculation unavailable');
      return core.parseQuoteResponse(await response.text(), response.headers.get('content-type') || '');
    }
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (!form.reportValidity() || !states.has(province.value)) return;
      const chosen = selection();
      const quantityInput = productForm.querySelector('[name="quantity"]');
      if (!chosen.variant?.available || !chosen.variant.requiresShipping ||
          !Number.isSafeInteger(chosen.quantity) || chosen.quantity < 1 ||
          !quantityInput?.reportValidity()) return;
      cancel();
      const current = ++sequence;
      const selectedStamp = signature();
      const zip = postcode.value.trim();
      const state = province.value;
      controller = new AbortController();
      const active = controller;
      const timeout = setTimeout(() => active.abort(), 25000);
      button.disabled = true;
      panel.setAttribute('aria-busy', 'true');
      status.textContent = panel.dataset.loading;
      try {
        if (panel.dataset.currency !== 'AUD') throw new Error('Unsupported currency');
        const cartResponse = await fetch(panel.dataset.cartUrl + '.js', {
          credentials: 'same-origin', cache: 'no-store', signal: active.signal,
        });
        if (!cartResponse.ok) throw new Error('Cart unavailable');
        const cart = await cartResponse.json();
        if (cart.currency !== 'AUD') throw new Error('Unsupported currency');
        if (!Number.isFinite(cart.total_weight) || !Number.isFinite(chosen.variant.weight)) {
          throw new Error('Unverified weight');
        }
        if (cart.total_weight + chosen.variant.weight * chosen.quantity > 20000) {
          status.textContent = panel.dataset.heavy;
          return;
        }
        if (chosen.sellingPlanId || cart.items.some(item => item.selling_plan_allocation)) {
          status.textContent = panel.dataset.subscription;
          return;
        }
        const lines = core.buildLines(cart, chosen);
        const created = await request(CREATE, {input: {
          lines, buyerIdentity: { countryCode: 'AU' },
          delivery: {addresses: [{ selected: true, oneTimeUse: true, validationStrategy: 'COUNTRY_CODE_ONLY',
            address: {deliveryAddress: {countryCode: 'AU', provinceCode: state, zip}} }]},
        }}, 'ETDeliveryQuote', active.signal);
        const quote = core.verifyQuoteCart(created, lines);
        const payload = await request(RATES, {id: quote.id}, 'ETDeliveryQuoteRates', active.signal);
        const groups = core.verifiedGroups(payload);
        if (current !== sequence || active.signal.aborted || signature() !== selectedStamp) return;
        if (!groups.length || groups.some(group => !group.options.length)) {
          status.textContent = panel.dataset.empty;
          return;
        }
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {style:'currency', currency:'AUD'});
        for (const [index, group] of groups.entries()) {
          if (groups.length > 1) {
            const heading = document.createElement('p');
            heading.className = 'etr-delivery__group';
            heading.textContent = panel.dataset.group.replace('__COUNT__', String(index + 1));
            results.append(heading);
          }
          const list = document.createElement('ul');
          list.className = 'etr-delivery__rates';
          for (const option of group.options) {
            const row = document.createElement('li');
            const label = document.createElement('span');
            const price = document.createElement('strong');
            label.textContent = option.title;
            price.textContent = money.format(option.cents / 100);
            row.append(label, price);
            list.append(row);
          }
          results.append(list);
        }
        results.hidden = false;
        status.textContent = panel.dataset.destination.replace('__POSTCODE__', zip);
        showSummary(groups, zip, state, money);
      } catch {
        if (current === sequence) {
          results.replaceChildren();
          results.hidden = true;
          status.textContent = panel.dataset.error;
        }
      } finally {
        clearTimeout(timeout);
        // Settled requests no longer need cancellation. An older finalizer must
        // never release the controller owned by a newer, still-active request.
        if (controller === active) controller = null;
        if (current === sequence) {
          button.disabled = false;
          panel.setAttribute('aria-busy', 'false');
        }
      }
    });
    form.addEventListener('input', () => cancel(true));
    form.addEventListener('change', () => cancel(true));
    for (const name of ['input','change']) productForm.addEventListener(name, () => {
      changed();
      requestAnimationFrame(changed);
    });
    productForm.addEventListener('click', () => requestAnimationFrame(changed));
    for (const name of ['cart:updated','ajaxProduct:added']) document.addEventListener(name, () => cancel(true));
    stamp = signature();
    changed();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once:true});
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
